import React, { ReactElement, useEffect, useMemo } from "react";

import styled from "styled-components";

import { ShapeTool } from "./bottomDock";
import {
  SELECTION_BLUE,
  StyledFormattingToolbar,
  StyledToolbarButton,
  StyledToolbarLink,
  stopClick,
} from "./elements";
import {
  InsertLinkIcon,
  OpenExpandedIcon,
  TrashIcon,
} from "../../components/Icons";
import { IStackedCardProps, StackedCard } from "../../components/StackedCard";
import { Tooltip } from "../../components/Tooltip";
import { Caption2 } from "../../components/Typography";
import {
  LinkCard,
  LinkDisplay,
  LinkPlaceholder,
  MOCK_LINKS,
  MockLink,
  useUnfurl,
} from "../stackedCard/content";
import { useInlineEdit } from "../stackedCard/useInlineEdit";
import linkCoverPhoto from "../static/stacked-card-cover.png";
import linkHeroPhoto from "../static/stacked-card-hero.png";

/* ------------------------------------------------------------------ */
/* Link element — StackedCard + the URL unfurl lifecycle               */
/* ------------------------------------------------------------------ */

/**
 * The only canvas element here built on the shipped design-system
 * `StackedCard`. The shell is hollow, so the board supplies the visual state
 * (`selected`) and the width, and the content comes from `LinkCard` — which
 * renders the unfurl lifecycle, not a static layout.
 *
 * Link cards are AUTO-HEIGHT. The host sets the width; the height follows the
 * content. That is why drawing a box commits only its width, and why resizing
 * is width-only.
 */

/* Width bounds shared by every drawn card (link, file). */
export const CARD_MIN_WIDTH = 180;
export const CARD_MAX_WIDTH = 420;

/* Below this the drag reads as a click, not a drawn box. */
export const CARD_DRAW_THRESHOLD = 8;

export const clampCardWidth = (width: number): number =>
  Math.min(CARD_MAX_WIDTH, Math.max(CARD_MIN_WIDTH, Math.round(width)));

export const LINK_DEFAULT_WIDTH = 260;

/* A shape check, nothing more. The real validation is server-side: the backend
   normalizes the URL, refuses anything but http(s), blocks private address
   space and re-checks every redirect hop. */
export const isValidLinkUrl = (value: string): boolean =>
  /^https?:\/\/[^\s/?#]+\.[^\s]*$/i.test(value.trim());

/* https://www.example.com/a?b → example.com */
export const linkDomain = (url: string): string =>
  url
    .trim()
    .replace(/^https?:\/\//i, "")
    .split(/[/?#]/)[0]
    .replace(/^www\./i, "");

/* Stand-in favicon / hero colours. A real unfurl stores the fetched favicon and
   OpenGraph image as attachments; here the colour is derived from the domain so
   every pasted host keeps a stable identity instead of sharing one colour. */
export const LINK_PALETTE = [
  "#0F6E56",
  "#8C5A2B",
  "#2F5DA8",
  "#8A3E6B",
  "#3F6B25",
  "#A8452F",
];

export const linkColor = (domain: string, offset: number): string => {
  let hash = 0;
  for (let index = 0; index < domain.length; index += 1) {
    hash = (hash * 31 + domain.charCodeAt(index)) % 997;
  }

  return LINK_PALETTE[(hash + offset) % LINK_PALETTE.length];
};

/* Hand-written seeds for the hosts worth demoing. Vimeo resolves to a playable
   video with a real cover image; the history link deliberately resolves without
   one, so the colour-cover fallback (a link whose page carries no OpenGraph
   image) is on the board too. */
export const KNOWN_LINKS: MockLink[] = [
  { ...MOCK_LINKS.vimeo, heroSrc: linkHeroPhoto },
  { ...MOCK_LINKS.history },
];

/**
 * The fake "server" response for a URL — what `GET /link-previews/...` would
 * eventually hold. Known hosts get their seed; anything else resolves to a
 * generic article keyed off its own domain, so pasting an arbitrary link still
 * produces a believable card instead of an empty one.
 */
export const resolveMockLink = (url: string): MockLink => {
  const domain = linkDomain(url);
  const known = KNOWN_LINKS.find((link) => linkDomain(link.url) === domain);

  if (known) {
    return { ...known, url };
  }

  return {
    url,
    domain,
    faviconColor: linkColor(domain, 0),
    heroColor: linkColor(domain, 3),
    heroSrc: linkCoverPhoto,
    title: `${domain} — the title a real unfurl reads from og:title`,
    description:
      "The summary an unfurl takes from og:description, falling back to the page's meta description when the site carries no OpenGraph tags.",
    kind: "Article",
    playable: false,
  };
};

/* Private address space — the one class of URL that passes a shape check and
   still can never resolve, because the server refuses to fetch it (and re-checks
   every redirect hop). Pasting one of these is how the error rung of the ladder
   is reachable from the board. */
export const PRIVATE_HOST =
  /^(?:10|127|192\.168|169\.254|172\.(?:1[6-9]|2\d|3[01]))\./;

export const isUnfurlable = (domain: string): boolean =>
  !PRIVATE_HOST.test(domain);

/** A link element on the board. Position lives in the shared positions map. */
export interface LinkElementDef {
  id: string;
  width: number;
  /** null while the card is an empty placeholder waiting for a URL. */
  url: string | null;
}

/* On the canvas a link is always the expanded card — hero, then the tinted
   footer band with the domain, title and description. Compact is a column-view
   density, so the board pins the level and shows no display switch. */
export const LINK_DISPLAY: LinkDisplay = "expanded";

/* The card fills the element box the board drew; the shell's reference 260px
   width gives way to the host. Height stays auto. */
export const StyledLinkCard = styled(StackedCard)`
  && {
    width: 100%;
  }

  user-select: none;
`;

/* The inline URL editor — the placeholder's interactive half. */
export const StyledLinkEditArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px 12px;
`;

export const StyledLinkInput = styled.input<{ $invalid?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1.5px solid
    ${({ $invalid }) =>
      $invalid ? "var(--red-alert)" : "var(--sc-selection-ring)"};
  border-radius: 6px;
  background: var(--page-paper-main);
  color: var(--color-theme-900);
  font-family: -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue",
    Arial, sans-serif;
  font-size: 13px;
  text-align: center;
  outline: none;

  &::placeholder {
    color: var(--color-theme-500);
  }
`;

export const StyledLinkHint = styled(Caption2)`
  display: block;
  text-align: center;
  color: var(--red-alert);
`;

/* The dashed rubber band drawn while the Link tool is armed. The width readout
   is the honest signal: the box sets the card's width, the height is the
   card's own business. */
/* The outline the app's ShapePreview draws — stroke only, the selection blue,
   at 70% — used both for the shape rubber band and the hover preview. */
export const StyledShapePreview = styled.div`
  position: absolute;
  box-sizing: border-box;
  pointer-events: none;

  svg {
    display: block;
    overflow: visible;
    fill: none;
    stroke: ${SELECTION_BLUE};
    stroke-width: 2;
    opacity: 0.7;
  }
`;

export const shapePreviewGlyph = (
  tool: ShapeTool,
  width: number,
  height: number
): ReactElement => (
  <svg width="100%" height="100%">
    {tool === "circle" ? <ellipse cx="50%" cy="50%" rx="50%" ry="50%" /> : null}
    {tool === "roundedRectangle" ? (
      <rect x="0" y="0" width="100%" height="100%" rx={8} ry={8} />
    ) : null}
    {tool === "triangle" ? (
      <polygon points={`${width / 2},0 ${width},${height} 0,${height}`} />
    ) : null}
  </svg>
);

export const StyledLinkDraft = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 1.5px dashed ${SELECTION_BLUE};
  border-radius: 8px;
  background-color: rgba(59, 130, 246, 0.08);
  pointer-events: none;
  /* No z-index on purpose: the canvas layer is not a stacking context, so a
     lifted draft would paint over the floating bars and the dock. */

  .draft-width {
    position: absolute;
    top: -22px;
    left: 0;
    padding: 1px 6px;
    border-radius: 4px;
    background-color: ${SELECTION_BLUE};
    color: #ffffff;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    white-space: nowrap;
  }
`;

export interface LinkFormattingToolbarProps {
  url: string;
  onEditUrl: () => void;
  onDelete: () => void;
}

/* The link element's floating toolbar. It acts on the ENTITY: go to the page,
   change which page, or bin the card. */
export const LinkFormattingToolbar = ({
  url,
  onEditUrl,
  onDelete,
}: LinkFormattingToolbarProps): ReactElement => (
  <StyledFormattingToolbar onMouseDown={stopClick} onClick={stopClick}>
    {/* Visit is a real anchor, not a button with a click handler: an external
        destination should behave like a link (middle-click, copy address,
        open in background) and `noopener` is the safe default for _blank. */}
    <StyledToolbarLink href={url} target="_blank" rel="noopener noreferrer">
      <OpenExpandedIcon className="i16" />
      Visit
    </StyledToolbarLink>
    <StyledToolbarButton type="button" onClick={onEditUrl}>
      <InsertLinkIcon className="i16" />
      Edit
    </StyledToolbarButton>
    <span className="divider" />
    <Tooltip title="Move to Trash">
      <StyledToolbarButton
        type="button"
        className="icon-only"
        onClick={onDelete}
      >
        <TrashIcon className="i16" />
      </StyledToolbarButton>
    </Tooltip>
  </StyledFormattingToolbar>
);

/**
 * The card's three resize callbacks, handed down untouched from the board. The
 * grip reports deltas measured from the gesture START and brackets the stream
 * with a start and a commit, so a card is only a conduit — the board resolves
 * what a delta means.
 */
export type CardResizeHandlers = Pick<
  IStackedCardProps,
  "onResizeStart" | "onResizeDelta" | "onResizeCommit"
>;

export interface LinkPreviewCardProps {
  element: LinkElementDef;
  selected: boolean;
  resize: CardResizeHandlers;
  onEditUrl: () => void;
  onDelete: () => void;
}

/**
 * A link element with a committed URL: the card runs the unfurl lifecycle.
 *
 * One controller per card, and the seed is memoized on the URL so the board's
 * own re-renders (drag, selection, a grid change) never restart the ladder.
 */
export const LinkPreviewCard = ({
  element,
  selected,
  resize,
  onEditUrl,
  onDelete,
}: LinkPreviewCardProps): ReactElement => {
  const url = element.url ?? "";
  const link = useMemo(() => resolveMockLink(url), [url]);
  const unfurl = useUnfurl({
    link,
    delayMs: 700,
    shouldFail: !isUnfurlable(link.domain),
  });

  return (
    <>
      <StyledLinkCard
        selected={selected}
        resizable={selected}
        resizeAxis="width"
        resizeLabel="Resize link card"
        {...resize}
      >
        <LinkCard unfurl={unfurl} display={LINK_DISPLAY} />
      </StyledLinkCard>
      {selected ? (
        <LinkFormattingToolbar
          url={url}
          onEditUrl={onEditUrl}
          onDelete={onDelete}
        />
      ) : null}
    </>
  );
};

export interface LinkElementProps {
  element: LinkElementDef;
  selected: boolean;
  /** Open the editor as soon as a freshly drawn placeholder lands. */
  autoEdit: boolean;
  onCommitUrl: (url: string) => void;
  resize: CardResizeHandlers;
  onDelete: () => void;
}

/**
 * The three phases of a link element:
 *
 *   placeholder  drawn but empty — an interactive invitation, distinct from the
 *                loading skeleton (spec §5)
 *   editing      the inline URL field, with the shell's `entered` ring and drag
 *                switched off
 *   preview      a URL is committed; the unfurl lifecycle takes over
 */
export const LinkElement = ({
  element,
  selected,
  autoEdit,
  onCommitUrl,
  resize,
  onDelete,
}: LinkElementProps): ReactElement => {
  const edit = useInlineEdit({
    validate: (value) =>
      isValidLinkUrl(value) ? true : "Enter a valid link, e.g. https://…",
    onCommit: (value) => onCommitUrl(value.trim()),
  });

  const { begin } = edit;

  // A box drawn with the Link tool is a request to add a link, so the editor
  // opens straight away; the placeholder is what a cancelled edit falls back to.
  useEffect(() => {
    if (autoEdit) {
      begin();
    }
  }, [autoEdit, begin]);

  /* Editing wins over the preview, so the same editor serves the first fill and
     every later correction: "Edit link" seeds the field with the current URL
     and Esc drops back to the card that was already there. */
  if (edit.editing) {
    return (
      <StyledLinkCard entered draggable={false} aria-label="Add a link">
        <StyledLinkEditArea>
          <StyledLinkInput
            {...edit.bind}
            type="text"
            inputMode="url"
            placeholder="https://…"
            $invalid={edit.error != null}
          />
          {edit.error ? (
            <StyledLinkHint role="alert">{edit.error}</StyledLinkHint>
          ) : (
            <Caption2 color="tertiary" style={{ textAlign: "center" }}>
              Enter to add · Esc to cancel
            </Caption2>
          )}
        </StyledLinkEditArea>
      </StyledLinkCard>
    );
  }

  if (element.url != null) {
    return (
      <LinkPreviewCard
        element={element}
        selected={selected}
        resize={resize}
        onEditUrl={() => {
          edit.reset(element.url ?? "");
          edit.begin();
        }}
        onDelete={onDelete}
      />
    );
  }

  return (
    <>
      <StyledLinkCard selected={selected} aria-label="Empty link card">
        {/* Fills the card, so it has to be draggable as well as clickable. */}
        <LinkPlaceholder data-drag-through="" onClick={edit.begin} />
      </StyledLinkCard>
      {/* Nothing to visit yet, so the empty card offers only the two options
          that mean anything: give it a URL, or bin it. */}
      {selected ? (
        <StyledFormattingToolbar onMouseDown={stopClick} onClick={stopClick}>
          <StyledToolbarButton type="button" onClick={edit.begin}>
            <InsertLinkIcon className="i16" />
            Edit
          </StyledToolbarButton>
          <span className="divider" />
          <Tooltip title="Move to Trash">
            <StyledToolbarButton
              type="button"
              className="icon-only"
              onClick={onDelete}
            >
              <TrashIcon className="i16" />
            </StyledToolbarButton>
          </Tooltip>
        </StyledFormattingToolbar>
      ) : null}
    </>
  );
};
